<script lang="ts" module>
  import { tv } from 'tailwind-variants';

  import Icon, { type IconName } from '@/ui/base/icon/Icon.svelte';

  export type InputType = 'text' | 'password';

  export interface InputLoginProps {
    label: string;
    value: string;
    onchange?: ChangeEventHandler<HTMLInputElement>;
    oninput?: ChangeEventHandler<HTMLInputElement>;

    type: InputType;
    id: string;
    errorMsg?: string;
    ariaLabel?: string;
  }

  export type ValidationResult = {
    result: boolean;
    errorMsg?: string;
  };

  export function isValidUserSelected(user: string | undefined): ValidationResult {
    if (user && user.length > 0) {
      return { result: true, errorMsg: undefined };
    } else {
      return {
        result: false,
        errorMsg: 'Bitte wählen Sie einen gültigen Benutzer aus.',
      };
    }
  }

  export function isValidPassword(password: string): ValidationResult {
    if (password.length > 0) return { result: true, errorMsg: undefined };
    else {
      return { result: false, errorMsg: 'Geben Sie bitte ein gültiges Passwort an.' };
    }
  }

  export function isValidEmail(email: string): ValidationResult {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (emailRegex.test(email)) return { result: true, errorMsg: undefined };
    else {
      return { result: false, errorMsg: 'Geben Sie bitte eine gültige E-Mail Adresse an.' };
    }
  }
</script>

<script lang="ts">
  import type { ChangeEventHandler } from 'svelte/elements';

  import BaseBtn from '@/ui/base/BaseBtn.svelte';

  let {
    id: idProp,
    value: value = $bindable(),
    type: typeProp,
    errorMsg: errorMsgProp = '',
    label: labelProp,
    ariaLabel: ariaLabelProp,
    ...props
  }: InputLoginProps = $props();

  let focused = $state(false);
  let passwordVisible = $state(false);
  let icon: IconName = $derived(passwordVisible ? 'Hide' : 'Show');

  let input: HTMLInputElement | undefined = undefined;
  $effect(() => {
    if (input !== undefined) {
      input.type =
        typeProp === 'text' || (typeProp === 'password' && passwordVisible) ? 'text' : 'password';
    }
  });

  const inputStyles = tv({
    base: 'relative h-12 w-full overflow-hidden rounded-t-sm border-b bg-dropdownlogin-H',
    variants: {
      focused: {
        true: 'border-c-login-open',
        false: 'border-c-login',
      },
      hasValue: {
        true: 'border-c-login-selected',
        false: '',
      },
      error: {
        true: '!border-c-login-error',
        false: '',
      },
    },
  });

  const textStyle = tv({
    base: 'pointer-events-none absolute left-4  cursor-text text-left font-sans font-normal transition-all duration-200 ease-in-out',
    variants: {
      labelPos: {
        up: 'top-1 translate-y-0 text-xs',
        down: 'top-1/2  -translate-y-1/2 select-none text-md',
      },
      error: {
        true: 'text-c-error',
        false: 'text-c-base',
      },
    },
  });
</script>

<div class="grid w-full gap-2">
  <div class={inputStyles({ focused, hasValue: value.length > 0, error: errorMsgProp.length > 0 })}>
    <input
      {...props}
      onfocus={() => (focused = true)}
      onblur={() => (focused = false)}
      bind:this={input}
      bind:value
      type="text"
      id={idProp}
      class="h-full w-full bg-transparent pl-4 pt-5 text-c-base caret-login-cursor hocus:!outline-none"
    />
    <label
      aria-label={ariaLabelProp}
      for={idProp}
      class={textStyle({
        labelPos: focused || value.length > 0 ? 'up' : 'down',
        error: errorMsgProp.length > 0,
      })}>{labelProp}</label
    >
    {#if typeProp === 'password'}
      <BaseBtn
        aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
        class="absolute right-3 top-1/2 -translate-y-1/2"
        onclick={() => {
          passwordVisible = !passwordVisible;
        }}
      >
        <Icon {icon} class="size-4"></Icon>
      </BaseBtn>
    {/if}
  </div>

  {#if errorMsgProp}
    <p class="px-4 font-sans text-xs font-normal text-c-error" role="alert">
      {errorMsgProp}
    </p>
  {/if}
</div>
